import React, { ChangeEvent, ChangeEventHandler, useCallback } from 'react';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
  type BaseFormFieldNodeProperties,
  type MasonryEngineNodeAction,
} from '@leagueplatform/masonry-engine';
import { Radio } from '@leagueplatform/genesis-core';
import { formatClassNames } from '#utils/format-masonry-class-name.util.ts';
import { useInvokeAction } from '../../../hooks/use-invoke-action.hook';

import { MasonryFieldsetWrapper } from '../masonry-fieldset-wrapper/masonry-fieldset-wrapper.component';
import { MasonryFormFieldWrapper } from '../masonry-form-field-renderer/masonry-form-field-wrapper.component';

type MasonryRadioOption = {
  label: string;
  value: string;
  disabled?: boolean;
  hint?: string;
};

type MasonryRadioNodeActions = {
  onChange?: MasonryEngineNodeAction;
};

type MasonryRadioNodeSections = {};
type MasonryRadioNodeProperties = BaseFormFieldNodeProperties<string> & {
  layout?: 'inline' | 'row';
  disabled?: boolean;
  options: MasonryRadioOption[];
};

export type MasonryRadioNode = MasonryEngineNode<
  'radioGroup',
  MasonryRadioNodeProperties,
  MasonryRadioNodeActions,
  MasonryRadioNodeSections
>;

export type MasonryRadioRendererProps =
  MasonryEngineNodeRendererProps<MasonryRadioNode>;

export const MasonryRadioRenderer = ({
  formFieldProperties,
  value: initialValue,
  layout,
  options,
  disabled,
  rootElementRef,
  onChange,
  className,
  engineClassName,
  locatorId,
  css,
}: MasonryRadioRendererProps) => {
  const { formFieldId, hint: fieldHint } = formFieldProperties;
  // Computed Props
  const value = initialValue;
  const orientation = layout === 'inline' ? 'row' : 'column';

  const invokeAction = useInvokeAction();

  // Event Handlers
  const setNodeState: ChangeEventHandler<HTMLInputElement> = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const { target } = event;

      await invokeAction({
        event,
        type: 'setNodeState',
        namespace: 'default',
        payload: { state: { value: target.value } },
      });

      if (onChange) {
        invokeAction({ event, ...onChange });
      }
    },
    [invokeAction, onChange],
  );

  const Wrapper =
    options?.length > 1 ? MasonryFieldsetWrapper : MasonryFormFieldWrapper;

  return (
    <Wrapper
      formFieldProperties={formFieldProperties}
      hint={fieldHint}
      rootElementRef={rootElementRef}
      disabled={disabled}
      css={{
        '.GDS-input-wrapper': { display: 'flex', flexDirection: orientation },
        ...css,
      }}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
    >
      {options?.map((option, optionIndex) => {
        const { label, value: optionValue, hint } = option;
        const optionId = `${formFieldId}-${optionIndex}`;
        return (
          <Radio
            key={optionId}
            id={optionId}
            name={formFieldId}
            label={label}
            value={optionValue}
            hint={hint}
            disabled={option.disabled}
            // eslint-disable-next-line eqeqeq
            checked={value == optionValue}
            onChange={setNodeState}
            layout={layout}
          />
        );
      })}
    </Wrapper>
  );
};
